import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeaders } from "../App";
import Layout from "../components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Edit, Trash2, Tag, Wrench, Palette } from "lucide-react";
import { toast } from "sonner";
import { DynamicIcon, ICON_NAMES } from "../components/shared/IconMap";

function CategorySection({ categories, onAdd, onEdit, onDelete }) {
  return (
    <div className="bg-white border border-[#E2E8F0] p-6" data-testid="category-section">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-heading font-bold text-xl text-[#0F172A]">Machine Categories</h2>
          <p className="text-sm text-[#64748B] mt-1">Categories appear as separate pages in the sidebar (e.g., SM Machines, NC Machines)</p>
        </div>
        <Button onClick={onAdd} className="bg-[#2563EB] hover:bg-[#0F172A] text-white rounded-none" data-testid="add-category-button">
          <Plus size={16} className="mr-2" />ADD CATEGORY
        </Button>
      </div>
      {categories.length === 0 ? (
        <p className="text-[#64748B] text-sm py-4">No categories defined. Add one to get started.</p>
      ) : (
        <div className="space-y-2">
          {categories.map((cat) => (
            <div key={cat.id} className="flex items-center justify-between p-4 border border-[#E2E8F0] hover:border-[#0F172A] transition-colors" data-testid={`category-row-${cat.id}`}>
              <div className="flex items-center gap-4">
                <DynamicIcon name={cat.icon || "Wrench"} size={18} className="text-[#64748B]" />
                <span className="px-3 py-1 text-sm font-bold tracking-wider" style={{ backgroundColor: cat.bg_color, color: cat.color }}>{cat.name}</span>
                <span className="text-[#0F172A] font-medium">{cat.label}</span>
                <div className="flex items-center gap-2 text-xs text-[#64748B]">
                  <Palette size={12} />
                  <span className="w-4 h-4 inline-block border" style={{ backgroundColor: cat.bg_color }}></span>
                  <span className="w-4 h-4 inline-block border" style={{ backgroundColor: cat.color }}></span>
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => onEdit(cat)} className="rounded-none border-[#E2E8F0]" data-testid={`edit-category-${cat.id}`}>
                  <Edit size={14} className="mr-1" />Edit
                </Button>
                <Button size="sm" variant="outline" onClick={() => onDelete(cat)} className="rounded-none border-[#DC2626] text-[#DC2626] hover:bg-[#DC2626] hover:text-white" data-testid={`delete-category-${cat.id}`}>
                  <Trash2 size={14} className="mr-1" />Delete
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TypeSection({ types, onAdd, onEdit, onDelete }) {
  return (
    <div className="bg-white border border-[#E2E8F0] p-6" data-testid="type-section">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-heading font-bold text-xl text-[#0F172A]">Machine Types</h2>
          <p className="text-sm text-[#64748B] mt-1">Types available in the machine creation form dropdown (e.g., CNC, Milling, Lathe)</p>
        </div>
        <Button onClick={onAdd} className="bg-[#2563EB] hover:bg-[#0F172A] text-white rounded-none" data-testid="add-type-button">
          <Plus size={16} className="mr-2" />ADD TYPE
        </Button>
      </div>
      {types.length === 0 ? (
        <p className="text-[#64748B] text-sm py-4">No types defined. Add one to get started.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {types.map((t) => (
            <div key={t.id} className="flex items-center justify-between p-3 border border-[#E2E8F0] hover:border-[#0F172A] transition-colors" data-testid={`type-row-${t.id}`}>
              <div className="flex items-center gap-3">
                <Wrench size={16} className="text-[#64748B]" />
                <span className="text-[#0F172A] font-medium text-sm">{t.name}</span>
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => onEdit(t)} className="h-8 w-8 p-0" data-testid={`edit-type-${t.id}`}>
                  <Edit size={14} />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => onDelete(t)} className="h-8 w-8 p-0 text-[#DC2626] hover:text-[#DC2626]" data-testid={`delete-type-${t.id}`}>
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function CategorySettingsPage({ onLogout, userRole, isFeatureEnabled, onCategoriesChanged }) {
  const [categories, setCategories] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);

  // Category dialog state
  const [catDialogOpen, setCatDialogOpen] = useState(false);
  const [editingCat, setEditingCat] = useState(null);
  const [catForm, setCatForm] = useState({ name: "", label: "", color: "#0F172A", bg_color: "#F1F5F9", icon: "Wrench" });

  // Type dialog state
  const [typeDialogOpen, setTypeDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [typeForm, setTypeForm] = useState({ name: "" });

  const fetchData = useCallback(async () => {
    try {
      const [catRes, typeRes] = await Promise.all([
        axios.get(`${API}/admin/machine-categories`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/machine-types`, { headers: getAuthHeaders() }),
      ]);
      setCategories(catRes.data);
      setTypes(typeRes.data);
    } catch {
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Category CRUD
  const openAddCategory = () => {
    setEditingCat(null);
    setCatForm({ name: "", label: "", color: "#0F172A", bg_color: "#F1F5F9", icon: "Wrench" });
    setCatDialogOpen(true);
  };
  const openEditCategory = (cat) => {
    setEditingCat(cat);
    setCatForm({ name: cat.name, label: cat.label, color: cat.color, bg_color: cat.bg_color, icon: cat.icon || "Wrench" });
    setCatDialogOpen(true);
  };
  const handleCatSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCat) {
        await axios.put(`${API}/admin/machine-categories/${editingCat.id}`, catForm, { headers: getAuthHeaders() });
        toast.success("Category updated");
      } else {
        await axios.post(`${API}/admin/machine-categories`, catForm, { headers: getAuthHeaders() });
        toast.success("Category created");
      }
      setCatDialogOpen(false);
      fetchData();
      if (onCategoriesChanged) onCategoriesChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save category");
    }
  };
  const handleDeleteCategory = async (cat) => {
    if (!window.confirm(`Delete category "${cat.name}"? Machines using this category will become uncategorized.`)) return;
    try {
      await axios.delete(`${API}/admin/machine-categories/${cat.id}`, { headers: getAuthHeaders() });
      toast.success(`Category "${cat.name}" deleted`);
      fetchData();
      if (onCategoriesChanged) onCategoriesChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete category");
    }
  };

  // Type CRUD
  const openAddType = () => {
    setEditingType(null);
    setTypeForm({ name: "" });
    setTypeDialogOpen(true);
  };
  const openEditType = (t) => {
    setEditingType(t);
    setTypeForm({ name: t.name });
    setTypeDialogOpen(true);
  };
  const handleTypeSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingType) {
        await axios.put(`${API}/admin/machine-types/${editingType.id}`, typeForm, { headers: getAuthHeaders() });
        toast.success("Type updated");
      } else {
        await axios.post(`${API}/admin/machine-types`, typeForm, { headers: getAuthHeaders() });
        toast.success("Type created");
      }
      setTypeDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save type");
    }
  };
  const handleDeleteType = async (t) => {
    if (!window.confirm(`Delete type "${t.name}"?`)) return;
    try {
      await axios.delete(`${API}/admin/machine-types/${t.id}`, { headers: getAuthHeaders() });
      toast.success(`Type "${t.name}" deleted`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete type");
    }
  };

  if (loading) {
    return (
      <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}>
        <div className="flex items-center justify-center h-64"><div className="text-[#64748B] font-mono">Loading settings...</div></div>
      </Layout>
    );
  }

  return (
    <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}>
      <div className="space-y-6" data-testid="category-settings-page">
        <div>
          <h1 className="font-heading font-black text-4xl text-[#0F172A] mb-2" data-testid="settings-title">Machine Settings</h1>
          <p className="text-[#64748B] text-sm">Manage machine categories and types. Changes apply system-wide.</p>
        </div>

        <CategorySection categories={categories} onAdd={openAddCategory} onEdit={openEditCategory} onDelete={handleDeleteCategory} />
        <TypeSection types={types} onAdd={openAddType} onEdit={openEditType} onDelete={handleDeleteType} />
      </div>

      {/* Category Dialog */}
      <Dialog open={catDialogOpen} onOpenChange={setCatDialogOpen}>
        <DialogContent className="bg-white rounded-none border border-[#E2E8F0]" aria-describedby="cat-form-desc">
          <DialogHeader><DialogTitle className="font-heading font-bold text-[#0F172A]">{editingCat ? "Edit Category" : "Add Category"}</DialogTitle></DialogHeader>
          <div id="cat-form-desc" className="sr-only">Form to manage machine categories</div>
          <form onSubmit={handleCatSubmit} className="space-y-4">
            <div>
              <Label className="text-[#0F172A]">Short Name (code) *</Label>
              <Input value={catForm.name} onChange={(e) => setCatForm({ ...catForm, name: e.target.value.toUpperCase() })} required maxLength={10} className="mt-1 rounded-none border-[#E2E8F0] font-mono uppercase" placeholder="SM" data-testid="cat-name-input" />
              <p className="text-xs text-[#64748B] mt-1">Uppercase code used in filtering and badges (e.g., SM, NC, HY)</p>
            </div>
            <div>
              <Label className="text-[#0F172A]">Display Label *</Label>
              <Input value={catForm.label} onChange={(e) => setCatForm({ ...catForm, label: e.target.value })} required className="mt-1 rounded-none border-[#E2E8F0]" placeholder="SM Machines" data-testid="cat-label-input" />
              <p className="text-xs text-[#64748B] mt-1">Shown in sidebar navigation and page titles</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#0F172A]">Text Color</Label>
                <div className="flex items-center gap-2 mt-1">
                  <input type="color" value={catForm.color} onChange={(e) => setCatForm({ ...catForm, color: e.target.value })} className="h-10 w-10 border-none cursor-pointer" data-testid="cat-color-input" />
                  <Input value={catForm.color} onChange={(e) => setCatForm({ ...catForm, color: e.target.value })} className="flex-1 rounded-none border-[#E2E8F0] font-mono text-xs" />
                </div>
              </div>
              <div>
                <Label className="text-[#0F172A]">Background Color</Label>
                <div className="flex items-center gap-2 mt-1">
                  <input type="color" value={catForm.bg_color} onChange={(e) => setCatForm({ ...catForm, bg_color: e.target.value })} className="h-10 w-10 border-none cursor-pointer" data-testid="cat-bgcolor-input" />
                  <Input value={catForm.bg_color} onChange={(e) => setCatForm({ ...catForm, bg_color: e.target.value })} className="flex-1 rounded-none border-[#E2E8F0] font-mono text-xs" />
                </div>
              </div>
            </div>
            <div className="p-3 bg-[#FAFAFA] border border-[#E2E8F0]">
              <p className="text-xs text-[#64748B] mb-2">Preview:</p>
              <div className="flex items-center gap-3">
                <DynamicIcon name={catForm.icon} size={18} className="text-[#64748B]" />
                <span className="px-3 py-1 text-sm font-bold tracking-wider" style={{ backgroundColor: catForm.bg_color, color: catForm.color }}>{catForm.name || "XX"}</span>
                <span className="text-sm text-[#0F172A]">{catForm.label || "Category Label"}</span>
              </div>
            </div>
            <div>
              <Label className="text-[#0F172A]">Icon</Label>
              <div className="mt-1 grid grid-cols-9 gap-1 max-h-36 overflow-y-auto p-2 border border-[#E2E8F0] bg-white">
                {ICON_NAMES.map((name) => (
                  <button
                    key={name}
                    type="button"
                    onClick={() => setCatForm({ ...catForm, icon: name })}
                    className={`p-2 flex items-center justify-center border transition-colors ${catForm.icon === name ? "border-[#2563EB] bg-[#EFF6FF]" : "border-transparent hover:bg-[#F1F5F9]"}`}
                    title={name}
                    data-testid={`icon-pick-${name}`}
                  >
                    <DynamicIcon name={name} size={16} className={catForm.icon === name ? "text-[#2563EB]" : "text-[#64748B]"} />
                  </button>
                ))}
              </div>
              <p className="text-xs text-[#64748B] mt-1">Selected: <span className="font-mono font-bold">{catForm.icon}</span></p>
            </div>
            <Button type="submit" className="w-full bg-[#2563EB] hover:bg-[#0F172A] text-white rounded-none" data-testid="submit-category-button">{editingCat ? "UPDATE" : "CREATE"}</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Type Dialog */}
      <Dialog open={typeDialogOpen} onOpenChange={setTypeDialogOpen}>
        <DialogContent className="bg-white rounded-none border border-[#E2E8F0]" aria-describedby="type-form-desc">
          <DialogHeader><DialogTitle className="font-heading font-bold text-[#0F172A]">{editingType ? "Edit Machine Type" : "Add Machine Type"}</DialogTitle></DialogHeader>
          <div id="type-form-desc" className="sr-only">Form to manage machine types</div>
          <form onSubmit={handleTypeSubmit} className="space-y-4">
            <div>
              <Label className="text-[#0F172A]">Type Name *</Label>
              <Input value={typeForm.name} onChange={(e) => setTypeForm({ ...typeForm, name: e.target.value })} required className="mt-1 rounded-none border-[#E2E8F0]" placeholder="CNC" data-testid="type-name-input" />
            </div>
            <Button type="submit" className="w-full bg-[#2563EB] hover:bg-[#0F172A] text-white rounded-none" data-testid="submit-type-button">{editingType ? "UPDATE" : "CREATE"}</Button>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
